// AdminHeader.tsx
import React, { useState, useEffect, useRef } from "react";
import { GoBell } from "react-icons/go";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../redux/slices/authAdminSlice";
import { AnimatePresence, motion } from "framer-motion";
import type { RootState, AppDispatch } from "../../redux/store";
import { LanguageSwitcher } from "../LanguageSwitcher/LanguageSwitcher";
import { useTranslation } from "react-i18next";

const AdminHeader = () => {
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<any | null>(null);
  const [page, setPage] = useState(1);
  const [notificationsToShow, setNotificationsToShow] = useState<any[]>([]);

  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();

  const user = useSelector((state: RootState) => state.auth.user);
  const notifications = useSelector((state: RootState) => state.notifications.items);
  const loading = useSelector((state: RootState) => state.notifications.loading);

  const notificationsPerPage = 10;

  const notificationRef = useRef<HTMLDivElement | null>(null);
  const userDropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const startIdx = (page - 1) * notificationsPerPage;
    const endIdx = page * notificationsPerPage;
    setNotificationsToShow(notifications.slice(0, endIdx));
  }, [notifications, page]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target as Node) &&
        userDropdownRef.current &&
        !userDropdownRef.current.contains(event.target as Node)
      ) {
        setIsNotificationOpen(false);
        setIsUserDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    dispatch(logout());
    setIsUserDropdownOpen(false);
  };

  const handleNotificationClick = (id: number) => {
    const notification = notifications.find((note) => note.id === id);
    setSelectedNotification(notification);
    setIsNotificationOpen(false);
  };

  const handleLoadMore = () => setPage(page + 1);

  return (
    <div className="flex gap-3 pt-2 pr-12">
      <div className="relative" ref={notificationRef}>
        <button
          onClick={() => {
            setIsNotificationOpen(!isNotificationOpen);
            setIsUserDropdownOpen(false);
          }}
          className="border w-[40px] h-[40px] flex justify-center items-center rounded-lg border-gray-200 hover:bg-gray-50 relative"
        >
          <GoBell className="text-xl" />
          {notifications.filter((n) => !n.read).length > 0 && (
            <span className="absolute top-0 right-0 inline-flex items-center justify-center w-2 h-2 bg-red-600 rounded-full transform translate-x-1/2 -translate-y-1/2"></span>
          )}
        </button>

        {isNotificationOpen && (
          <div className="absolute right-0 z-10 w-72 mt-2 origin-top-right bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 max-h-80 overflow-auto">
            {loading ? (
              <div className="p-4 text-sm text-gray-500">
                {t("notification.loading")}
              </div>
            ) : notificationsToShow.length === 0 ? (
              <div className="p-4 text-sm text-gray-500">
                {t("notification.noNotifications")}
              </div>
            ) : (
              notificationsToShow.map((note: any) => (
                <div
                  key={note.id}
                  className={`p-3 border-b last:border-b-0 hover:bg-gray-50 cursor-pointer ${note.read ? "bg-gray-100" : ""}`}
                  onClick={() => handleNotificationClick(note.id)}
                >
                  <p className="font-medium text-gray-800">{note.title}</p>
                  <p className="text-sm text-gray-600 truncate">{note.message}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {new Date(note.createdAt).toLocaleString()}
                  </p>
                </div>
              ))
            )}
            {!loading && notificationsToShow.length < notifications.length && (
              <button
                onClick={handleLoadMore}
                className="w-full py-2 text-sm text-center text-blue-500 hover:bg-blue-50"
              >
                {t("notification.loadMore")}
              </button>
            )}
            {!loading && notificationsToShow.length === notifications.length && (
              <div className="p-3 text-center text-sm text-gray-500">
                {t("notification.noMore")}
              </div>
            )}
          </div>
        )}
      </div>

      <LanguageSwitcher />

      {/* User Dropdown */}
      <div className="relative inline-block text-left" ref={userDropdownRef}>
        <button
          onClick={() => {
            setIsUserDropdownOpen(!isUserDropdownOpen);
            setIsNotificationOpen(false);
          }}
          className="inline-flex w-full justify-center items-center gap-x-1.5 rounded-md bg-white px-3 h-[40px] text-sm font-medium text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 hover:bg-gray-50"
        >
          {user ? user.name : ""}
          <svg className="w-5 h-5 -mr-1 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
              clipRule="evenodd"
            />
          </svg>
        </button>
        {isUserDropdownOpen && (
          <div className="absolute right-0 z-10 w-32 mt-2 origin-top-right bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5">
            <button
              className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100"
              onClick={handleLogout}
            >
              {t("notification.logout")}
            </button>
          </div>
        )}
      </div>

      {/* Notification Modal */}
      <AnimatePresence>
        {selectedNotification && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedNotification(null)}
          >
            <motion.div
              className="bg-white p-6 rounded-2xl shadow-xl max-w-md w-full relative"
              initial={{ scale: 0.8, rotateX: -90, opacity: 0 }}
              animate={{
                scale: 1,
                rotateX: 0,
                opacity: 1,
                transition: { type: "spring", stiffness: 150 },
              }}
              exit={{ scale: 0.8, rotateX: -90, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-lg font-bold text-gray-900 mb-2">
                {selectedNotification.title}
              </h2>
              <p className="text-gray-700 mb-4">
                {selectedNotification.message}
              </p>
              <p className="text-xs text-gray-400">
                {new Date(selectedNotification.createdAt).toLocaleString()}
              </p>
              <button
                className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition"
                onClick={() => setSelectedNotification(null)}
              >
                {t("notification.close")}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminHeader;
